import { IDisposable } from '@sm/max-common'
import { MaxEvent } from '@platform/events'
import { DataStore, IDataStore } from './data'
import { I18nTextStore } from './i18n'
import { set, get } from '../utils/object'

export interface Context extends IDisposable {
  readonly max: any
  readonly _proxy: any
  readonly pageCode: string
  readonly store: IDataStore
  register: (mayName: string, mayOrigin: any, mayFn?: any) => IDisposable
}

const root = 'max'

function registerScopeData(
  register: (mayName: string, mayOrigin: any, mayFn?: any, mayScope?: string) => IDisposable,
  store: IDataStore,
  scope: string,
) {
  register(`${scope}.get`, store.get)
  register(`${scope}.set`, store.set)
  register(`${scope}.clear`, store.clear)
  register(`${scope}.merge`, store.merge)
  register(`${scope}.onChange`, store.onChange)
}

export function Context(
  localMap: Record<string, any>,
  isPage: boolean,
  key = '',
  code = '',
  _isGlobal = false,
): Context {
  const ctx: any = {}
  const locale = I18nTextStore(localMap)
  const isGlobal = _isGlobal
  const shortKey = key
  const pageCode = code
  const store = DataStore(pageCode, _isGlobal)
  const event = MaxEvent()

  const register = (mayName: string, mayOrigin: any, mayFn?: any, mayScope?: string): IDisposable => {
    if (!mayName) return
    if (mayScope && mayScope === 'root') return _default.register(mayName, mayOrigin, mayFn)
    const origin = typeof mayOrigin === 'string' ? mayFn : mayOrigin
    const name = typeof mayOrigin === 'string' ? mayOrigin : mayName
    const path = name.split('.')
    const key = path.pop()
    const absolute = `${root}${path.length === 0 ? '' : `.${path.join('.')}`}`

    let next: any = get(ctx, absolute) || {}
    if (key === '*') {
      // 支持iot的特殊需求
      next = new Proxy(
        {},
        {
          has: () => true,
          get: (_, property) => {
            return origin.bind({ property })
          },
        },
      )
    } else {
      // 非iot还是使用方法名作为索引
      next = {
        ...next,
        [key]: origin,
      }
    }
    set(ctx, absolute, next)
    return {
      dispose() {
        delete next[key]
      },
    }
  }

  const dispose = () => {
    if (_isGlobal) return
    store.dispose()
    event.dispose()
    // ctx = {}
  }

  // data
  register('data.set', store.set)
  register('data.get', store.get)
  register('data.clear', store.clear)
  register('data.merge', store.merge)
  register('data.onChange', store.onChange)

  if (!isGlobal) {
    if (isPage) {
      // page
      registerScopeData(register, store, 'pageData')
    } else {
      // composite
      registerScopeData(register, store, 'compData')
    }
    registerScopeData(register, _default.store, 'appData')
  } else {
    // app
    registerScopeData(register, store, 'appData')
  }

  // i18n
  register('i18n.setLocaleMap', locale.setLocaleMap)
  register('i18n.getLocaleMap', locale.getLocaleMap)
  // 需要特殊处理一下
  register('i18n.getText', (key: string, format?: string | string[] | { [key: string]: string }) => {
    const _text = locale.getText(key, format)
    if (typeof _text === 'undefined' && !isGlobal) {
      const finalKey = shortKey ? `${shortKey}:${key}` : key
      return _default.max.i18n.getText(finalKey, format)
    }
    return _text || key
  })
  register('i18n.transformLang', (key: string) => {
    const _text = locale.transformLang(key)
    if (typeof _text === 'undefined' && !isGlobal) {
      return _default.max.i18n.transformLang(key)
    }
    return _text
  })

  // events
  register('event.on', event.on)
  register('event.off', event.off)
  register('event.emit', event.emit)
  register('event.once', event.once)

  // 暴露register
  register('register', register)
  register('has', (item: Function) => Boolean(item))

  return {
    get max() {
      return ctx[root]
    },
    get _proxy() {
      return {}
    },
    get pageCode() {
      return pageCode
    },
    get store() {
      return store
    },
    register,
    dispose,
  }
}

export const _default = Context({}, false, '', '', true)
export const create = (localMap: Record<string, any>, isPage: boolean) => Context(localMap, isPage)

